import { motion } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import { kuiperClassNames } from '@opallabs/kuiper'
import { getModuleIds } from '../../Content/helpers/functions/getModuleIds'
import {
  GetSectionPostOptionsProps,
  OnDragDropProps,
  OnShowAssetPickerProps,
  SectionPostOptions,
} from '../../Content/helpers/types'
import type { ModuleDefinition, Module } from '../../Content/helpers/types'
import { CarouselSection } from '../CarouselSection'
import { styles as sharedStyles } from '../sharedStyles/styles'
import { AnimationProps } from '../types'
import { animations } from './animations'
import { dragImage } from './dragImage'
import { styles } from './styles'

export interface ExpandedCarouselBodyProps {
  allProps: Record<string, unknown>
  availableModules: ModuleDefinition[]
  carouselWidth: number
  getModule?: (args: GetSectionPostOptionsProps) => SectionPostOptions
  isPresenting: boolean
  modules: Module[]
  onDeleteSection: (indexToRemove: number) => void
  onDragDrop: OnDragDropProps
  onExpandAsset: (assetId: number) => void
  onRemoveAsset: (assetId: number, index: number) => void
  onShowAssetPicker: (args: OnShowAssetPickerProps) => void
  onTooltipToggle: (index: number) => void
  readOnly: boolean
  shouldExit: boolean
  tooltipActive: number
  updateSectionPostOption: (index: number, value: string, name: string) => void
}

export const ExpandedCarouselBody: React.FC<ExpandedCarouselBodyProps> = ({
  allProps,
  availableModules,
  carouselWidth,
  getModule,
  isPresenting,
  tooltipActive,
  modules,
  onDeleteSection,
  onDragDrop,
  onExpandAsset,
  onRemoveAsset,
  onShowAssetPicker,
  onTooltipToggle,
  readOnly,
  shouldExit,
  updateSectionPostOption,
}) => {
  const [moduleIds, setModuleIds] = useState(getModuleIds(modules))
  const [dragState, setDragState] = useState(false)
  const [dragZone, setDragZone] = useState(-1)
  const [draggedComp, setDraggedComp] = useState(-1)
  const [draggingModules, setDraggingModules] = useState(modules)
  const dragImageElement: HTMLImageElement = new Image()
  dragImageElement.src = dragImage

  const modulesLength = useRef(modules.length)
  const moduleRef = useRef<HTMLDivElement>(null)
  const exitAnimationAttr = useRef<AnimationProps>({})

  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    index: number
  ) => {
    // using setData is required to make Firefox happy
    e.dataTransfer?.setData('text', '')
    e.dataTransfer?.setDragImage(dragImageElement, 173, 36)

    setDragState(true)
    setDraggedComp(index)
  }

  const handleDragEnter = (index: number) => {
    if (dragState) {
      const newDraggingModules = [...modules]
      const draggedModule = newDraggingModules.splice(draggedComp, 1)[0]
      newDraggingModules.splice(index, 0, draggedModule)
      setDraggingModules(newDraggingModules)
      setDragZone(index)
    }
  }

  const handleDragLeave = () => {
    if (dragState) setDragZone(-1)
  }

  const handleSaveSectionOption = (
    index: number,
    value: string,
    name: string
  ) => {
    updateSectionPostOption(index, value, name)
    // on a section reorder, assign new keys and force a re-render
    if (name === 'section position') setModuleIds(getModuleIds(modules))
  }

  const handleDrop = (dropLocation: string) => {
    if (dragState && dropLocation === 'valid') {
      updateSectionPostOption(
        draggedComp,
        dragZone.toString(),
        'section position'
      )
    }
    setDragState(false)
    setDraggingModules(modules)
    setModuleIds(getModuleIds(modules))
    setDraggedComp(-1)
    setDragZone(-1)
  }

  useEffect(() => {
    setDraggingModules(modules)
    if (modulesLength.current < modules.length && moduleRef?.current) {
      moduleRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' })
      setModuleIds(getModuleIds(modules))
      modulesLength.current = modules.length
    }
  }, [modules])

  useEffect(() => {
    if (shouldExit) {
      exitAnimationAttr.current = animations.exitAnimation
    } else {
      exitAnimationAttr.current = {}
    }
  }, [shouldExit])

  return (
    <div
      className={kuiperClassNames('expanded-asset-carousel')}
      onDragOver={(e) => e.preventDefault()}
      onDrop={() => handleDrop('invalid')}
    >
      <ol css={styles.getExpandedWrapper(isPresenting || readOnly)}>
        {draggingModules.map((module, index) => (
          <motion.li
            key={`${
              (module?.componentName || module?.name) as string
            }-${index}-${moduleIds ? moduleIds[index] : 0}`}
            css={styles.getCarouselSection({
              width: carouselWidth,
              isReadOnly: readOnly || isPresenting,
              shouldExit: shouldExit,
              index: index,
            })}
            {...(shouldExit ? exitAnimationAttr.current : {})}
          >
            <div
              css={sharedStyles.getIndexIndicator(
                true,
                carouselWidth,
                readOnly || isPresenting
              )}
            >
              {index + 1}
            </div>
            {dragState && dragZone === index && (
              <div
                css={styles.getDropZone(carouselWidth)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleDrop('valid')}
                onDragLeave={handleDragLeave}
              >
                <p>Release to place</p>
              </div>
            )}
            <CarouselSection
              key={`${
                (module?.componentName || module?.name) as string
              }-${index}-${moduleIds ? moduleIds[index] : 0}`}
              allProps={allProps}
              availableModules={availableModules}
              index={index}
              width={carouselWidth}
              getModule={getModule}
              isPresenting={isPresenting}
              module={module}
              isExpanded={true}
              isDragging={dragState}
              modules={modules || []}
              onDeleteSection={(index: number) => onDeleteSection(index)}
              onDragEnter={() => handleDragEnter(index)}
              onDragStart={(e: React.DragEvent<HTMLDivElement>) =>
                handleDragStart(e, index)
              }
              onExpandAsset={onExpandAsset}
              onRemoveAsset={onRemoveAsset}
              onDragDrop={onDragDrop}
              onShowAssetPicker={onShowAssetPicker}
              onTooltipToggle={(index) => onTooltipToggle(index)}
              readOnly={readOnly}
              tooltipActive={tooltipActive === index}
              updateSectionOptions={handleSaveSectionOption}
            />
            {index === modules.length - 1 && <div ref={moduleRef} />}
          </motion.li>
        ))}
      </ol>
    </div>
  )
}
